// Careful, this package only defines a type entry point, but the `Chargebee` export is no type,
// so we get around this by still using a type import, but treating the `Chargebee` export as a value.
import type { Chargebee, ChargebeeInstance, PortalCallbacks } from '@chargebee/chargebee-js-types'
import type { CbPortalForwardOptions } from '@chargebee/chargebee-js-types/cb-types/interfaces/cb-portal-options'

declare global {
  interface Window {
    Chargebee?: typeof Chargebee
  }
}

export type SectionType = 'PAYMENT_SOURCES' | 'BILLING_HISTORY' | 'ADDRESS'
export interface PortalCallbacks {
  onClose?: () => void
  onPaymentSourceAdd?: () => void
  onPaymentSourceUpdate?: () => void
}

export default function useChargebee(callbacks: PortalCallbacks = {}) {
  const { chargebee: chargebeeSettings } = useRuntimeConfig().public
  const { logError } = useErrorHandling()
  const { getActiveSubscription } = usePaymentStore()

  const cbInstance = ref<ChargebeeInstance | null>(null)
  let resolveChargebee: ((instance: ChargebeeInstance | null) => void) | null = null
  const shouldResolve = new Promise<ChargebeeInstance | null>(resolve => {
    resolveChargebee = resolve
  })

  const { load, onLoaded } = useScript(
    {
      src: 'https://js.chargebee.com/v2/chargebee.js',
      type: 'text/javascript',
      async: true,
      defer: true,
    },
    {
      trigger: 'manual',
      use() {
        return window.Chargebee
      },
    }
  )

  onLoaded(library => {
    if (library.inited) {
      cbInstance.value = library.getInstance()
      resolveChargebee?.(cbInstance.value)
      return
    }

    try {
      library.init(chargebeeSettings)
      cbInstance.value = library.getInstance()
      resolveChargebee?.(cbInstance.value)
    } catch (error) {
      logError('Chargebee initialisation failed', error)
      resolveChargebee?.(null)
    }
  })

  const createPortalCallbacks = (): PortalCallbacks => {
    return {
      close() {
        cbInstance.value?.logout()
        callbacks.onClose?.()
      },
      loaded() {},
      visit() {},
      paymentSourceAdd() {
        callbacks.onPaymentSourceAdd?.()
      },
      paymentSourceUpdate() {
        callbacks.onPaymentSourceUpdate?.()
      },
      paymentSourceRemove() {},
      subscriptionChanged() {},
      subscriptionCustomFieldsChanged() {},
      subscriptionCancelled() {},
      subscriptionResumed() {},
      subscriptionPaused() {},
      scheduledPauseRemoved() {},
      scheduledCancellationRemoved() {},
      subscriptionReactivated() {},
    }
  }

  const { businessRepository } = useApi()

  const openPortal = async (businessId: string, type?: SectionType): Promise<void> => {
    try {
      const activeSubscription = await getActiveSubscription(businessId)
      if (!activeSubscription) return

      const cbSession = await businessRepository.createChargebeeSessionPortal(businessId)
      await cbInstance.value!.setPortalSession(cbSession)
      const cbPortal = cbInstance.value!.createChargebeePortal()

      if (type) {
        const forwardOptions: CbPortalForwardOptions = {
          sectionType: window.Chargebee.getPortalSections()[type],
          params: {
            subscriptionId: activeSubscription,
          },
        }
        cbPortal.openSection(forwardOptions, createPortalCallbacks())
      } else {
        cbPortal.open(createPortalCallbacks())
      }
    } catch (error) {
      logError('Cannot open Chargebee portal', error)
    }
  }

  const loadChargebee = (): Promise<ChargebeeInstance | null> => {
    if (!chargebeeSettings.site || !chargebeeSettings.publishableKey) {
      logError('Chargebee settings missing', new Error('Chargebee initialisation failed'))
      resolveChargebee?.(null)
      return shouldResolve
    }

    load()
    return shouldResolve
  }

  onMounted(() => {
    loadChargebee()
  })

  onUnmounted(() => {
    cbInstance.value = null
  })

  return {
    openPortal,
    loadChargebee,
    cbInstance,
  }
}
